import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, FileText, Wrench } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IncidentReport } from "../../../../features/incident/@types";
import { useGetIncidentReportsMutation } from "../../../../features/incident/incidentReportApiSlice";
import { Maintenance } from "../../../../features/maintenance/@types";
import { useGetMaintenancesMutation } from "../../../../features/maintenance/maintenanceApiSlice";
import { Requisition } from "../../../../features/requisition/@types";
import { useGetReviewRequisitionsMutation } from "../../../../features/requisition/requisitionApiSlice";
import {
  extractSearchItems,
  extractTotalCount,
  formatRelativeTime,
} from "./headerUtils";

interface HeaderNotificationsProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

type NotificationItem = {
  id: string;
  title: string;
  subtitle: string;
  time?: string;
  path: string;
  icon: "requisition" | "incident" | "maintenance";
};

const HeaderNotifications: React.FC<HeaderNotificationsProps> = ({
  isOpen,
  onToggle,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCompanyAdmin = location.pathname.startsWith("/admin");

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [getReviewRequisitions] = useGetReviewRequisitionsMutation();
  const [getIncidentReports] = useGetIncidentReportsMutation();
  const [getMaintenances] = useGetMaintenancesMutation();

  const fetchNotifications = useCallback(async () => {
    if (!isCompanyAdmin) {
      setItems([]);
      setBadgeCount(0);
      return;
    }

    setIsLoading(true);
    const request = { pageNumber: 0, pageSize: 5 };

    try {
      const [requisitionsRes, incidentsRes, maintenanceRes] =
        await Promise.allSettled([
          getReviewRequisitions(request).unwrap(),
          getIncidentReports(request).unwrap(),
          getMaintenances({ ...request, status: "Pending" }).unwrap(),
        ]);

      const nextItems: NotificationItem[] = [];
      let nextBadge = 0;

      if (requisitionsRes.status === "fulfilled") {
        const requisitions = extractSearchItems<Requisition>(
          requisitionsRes.value
        );
        nextBadge += extractTotalCount(requisitionsRes.value) || requisitions.length;
        requisitions.forEach((item) => {
          nextItems.push({
            id: `requisition-${item.id}`,
            title: item.requisitionNumber || "Requisition pending review",
            subtitle: item.description || item.payTo || "Awaiting approval",
            time: formatRelativeTime(item.createdOn),
            path: "/admin/requisition?tab=reviews",
            icon: "requisition",
          });
        });
      }

      if (incidentsRes.status === "fulfilled") {
        const incidents = extractSearchItems<IncidentReport>(
          incidentsRes.value
        );
        incidents.forEach((item) => {
          const isRecent =
            item.createdOn &&
            Date.now() - new Date(item.createdOn).getTime() <
              48 * 60 * 60 * 1000;
          if (isRecent) nextBadge += 1;
          nextItems.push({
            id: `incident-${item.id}`,
            title: item.reportType || "Incident reported",
            subtitle: [item.driverName, item.location, item.severity]
              .filter(Boolean)
              .join(" · "),
            time: formatRelativeTime(item.createdOn),
            path: "/admin/incident-report",
            icon: "incident",
          });
        });
      }

      if (maintenanceRes.status === "fulfilled") {
        const jobs = extractSearchItems<Maintenance>(maintenanceRes.value);
        nextBadge += extractTotalCount(maintenanceRes.value) || jobs.length;
        jobs.forEach((item) => {
          nextItems.push({
            id: `maintenance-${item.id}`,
            title: item.maintenanceType || "Maintenance pending",
            subtitle: [item.vehicle, item.driverName || item.driver]
              .filter(Boolean)
              .join(" · "),
            path: "/admin/maintenance",
            icon: "maintenance",
          });
        });
      }

      setItems(nextItems);
      setBadgeCount(nextBadge);
    } finally {
      setIsLoading(false);
    }
  }, [
    getIncidentReports,
    getMaintenances,
    getReviewRequisitions,
    isCompanyAdmin,
  ]);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (isOpen && isCompanyAdmin) void fetchNotifications();
  }, [fetchNotifications, isCompanyAdmin, isOpen]);

  const iconMap = useMemo(
    () => ({
      requisition: FileText,
      incident: AlertTriangle,
      maintenance: Wrench,
    }),
    []
  );

  const handleOpenItem = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <div className="relative">
      <motion.button
        type="button"
        whileTap={{ scale: 0.95 }}
        onClick={onToggle}
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-900"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          className="h-[22px] w-[22px]"
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M2.53 14.394c-.213 1.353.738 2.292 1.902 2.76c4.463 1.795 10.673 1.795 15.136 0c1.164-.468 2.115-1.407 1.902-2.76c-.13-.832-.777-1.524-1.256-2.2c-.627-.897-.689-1.874-.69-2.915C19.525 5.26 16.157 2 12 2S4.475 5.26 4.475 9.28c0 1.04-.062 2.018-.69 2.914c-.478.676-1.124 1.368-1.255 2.2M9 21c.796.622 1.848 1 3 1s2.204-.378 3-1"
          />
        </svg>
        {badgeCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="fixed left-4 right-4 top-[64px] z-50 rounded-2xl md:absolute md:left-auto md:right-0 md:top-[calc(100%+8px)] md:w-[360px] border border-gray-100 bg-white shadow-xl"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-hColor">
                Notifications
              </h3>
              {badgeCount > 0 && (
                <span className="text-xs text-primary font-medium">
                  {badgeCount} new
                </span>
              )}
            </div>

            <div className="max-h-[360px] overflow-y-auto">
              {isLoading && items.length === 0 ? (
                <p className="px-4 py-8 text-sm text-center text-pColor">
                  Loading notifications...
                </p>
              ) : items.length === 0 ? (
                <p className="px-4 py-8 text-sm text-center text-pColor">
                  You're all caught up
                </p>
              ) : (
                items.map((item) => {
                  const Icon = iconMap[item.icon];
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleOpenItem(item.path)}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0"
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EDF2DFB2] text-primary">
                        <Icon size={16} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-hColor truncate">
                          {item.title}
                        </span>
                        <span className="block text-xs text-pColor truncate">
                          {item.subtitle}
                        </span>
                      </span>
                      {item.time && (
                        <span className="shrink-0 text-[11px] text-pColor">
                          {item.time}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {isCompanyAdmin && (
              <div className="border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => handleOpenItem("/admin/settings?tab=notifications")}
                  className="w-full px-4 py-3 text-sm font-medium text-primary hover:bg-gray-50"
                >
                  Notification settings
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeaderNotifications;
